import { motion } from "framer-motion";
import {
  Code2,
  Gamepad2,
  Building2,
  Glasses,
  Cpu,
  ArrowUpRight,
} from "lucide-react";

const VR_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3c26e507-38ce-4d09-be4d-b859e821e069/images/5317d96ddab838860d5bde86b80d7240b1df1803cbd17b9fd416502be2902863.png";
const REALTIME_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3c26e507-38ce-4d09-be4d-b859e821e069/images/8c985d99a9fb0a23aa9f60607aa2fb8bfb3d9acca2f89b2645ff639bfdaf510e.png";

const SERVICES = [
  {
    title: "Custom 3D Software",
    desc: "Bespoke engines, viewers and tools — engineered for your exact pipeline, content, and platform.",
    icon: Code2,
    accent: "orange",
    span: "lg:col-span-2 lg:row-span-2",
    testid: "service-custom-3d",
    bg: REALTIME_IMG,
  },
  {
    title: "Real-Time 3D Apps",
    desc: "GPU-tuned, 60+ fps experiences for web, desktop and embedded systems.",
    icon: Cpu,
    accent: "green",
    span: "lg:col-span-2",
    testid: "service-realtime",
  },
  {
    title: "Games & Interactive Systems",
    desc: "From prototype to production — Unity, Unreal, and custom interactive systems.",
    icon: Gamepad2,
    accent: "orange",
    span: "lg:col-span-2",
    testid: "service-games",
  },
  {
    title: "Architecture & Industrial 3D Visualization",
    desc: "Photoreal walkthroughs, digital twins and configurators for AEC and heavy industry.",
    icon: Building2,
    accent: "green",
    span: "lg:col-span-2",
    testid: "service-archviz",
  },
  {
    title: "Virtual & Augmented Reality",
    desc: "VR training, AR product showrooms and XR experiences with rock-solid presence.",
    icon: Glasses,
    accent: "orange",
    span: "lg:col-span-2",
    testid: "service-vrar",
    bg: VR_IMG,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8"
    >
      <div className="absolute -top-20 right-0 h-[400px] w-[400px] rounded-full bg-[#FF6B00]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66]">
              Capabilities
            </div>
            <h2
              className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter max-w-3xl"
              data-testid="services-headline"
            >
              Software that thinks in <span className="text-gradient">three dimensions.</span>
            </h2>
          </div>
          <p className="max-w-md text-gray-400 text-base">
            A focused stack of services covering the full lifecycle of a modern
            3D product — from architecture to shipping a 60 fps build.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[minmax(220px,auto)]"
        >
          {SERVICES.map((s) => {
            const Icon = s.icon;
            const isOrange = s.accent === "orange";
            return (
              <motion.article
                key={s.title}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.55 } },
                }}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#101010] p-6 sm:p-8 hover:border-${
                  isOrange ? "[#FF6B00]" : "[#00FF66]"
                }/40 hover:-translate-y-1 transition-all duration-300 ${s.span}`}
                data-testid={s.testid}
              >
                {s.bg && (
                  <>
                    <img
                      src={s.bg}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-25 group-hover:opacity-40 transition-opacity duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
                  </>
                )}

                <div className="relative flex h-full flex-col justify-between gap-8">
                  <div className="flex items-start justify-between">
                    <div
                      className={`inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 ${
                        isOrange
                          ? "bg-[#FF6B00]/10 text-[#FF6B00]"
                          : "bg-[#00FF66]/10 text-[#00FF66]"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-gray-500 group-hover:text-white transition-colors" />
                  </div>

                  <div>
                    <h3 className="font-[Unbounded] text-xl sm:text-2xl font-semibold leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm sm:text-[15px] text-gray-400 leading-relaxed max-w-md">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
